import type { ReactNode } from 'react';
import type { ScanDetail } from '@/components/smartai/api/getScanDetails';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import {
  BuildDiffCardHeader,
  ModeToggle,
} from '@/components/smartai/ComponentBuildDiff/build-diff-layout';

export { BuildDiffCardHeader, ModeToggle };

/** Test Gap page rail — same gutters as other workspace pages. */
export const testGapPageClass = onboardingPageClass;

/** Compact card shell on laptop; full padding on large desktops. */
export const testGapCardClass = cn(
  'min-w-0 rounded-xl border border-[var(--inv-border)] bg-[var(--inv-surface)] p-3 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]',
  '2xl:rounded-[14px] 2xl:p-4',
);

export const testGapStatsGridClass = cn(
  'mb-4 grid grid-cols-1 gap-3 min-[640px]:grid-cols-2 min-[1280px]:grid-cols-3',
  '2xl:mb-6 2xl:gap-4',
);

export const testGapBreakdownGridClass =
  'grid grid-cols-1 gap-3 min-[640px]:grid-cols-2 2xl:gap-4';

export const testGapBreakdownDetailGridClass = cn(
  'mt-3 grid grid-cols-1 gap-3 min-[640px]:grid-cols-2 min-[900px]:grid-cols-3',
  '2xl:mt-4 2xl:gap-4',
);

export const testGapSectionTitleClass = cn(
  'mb-3 font-serif text-[1rem] font-semibold text-[var(--inv-text)]',
  '2xl:mb-5 2xl:text-[1.125rem]',
);

export const testGapSubsectionTitleClass = cn(
  'm-0 font-serif text-[0.9375rem] font-semibold text-[var(--inv-text)]',
  '2xl:text-[1.0625rem]',
);

export const testGapResultsSectionClass = cn(testGapCardClass, 'mt-4 2xl:mt-5');

interface CompactProgressBarProps {
  value: number;
  tone?: 'accent' | 'danger';
  className?: string;
}

export function CompactProgressBar({
  value,
  tone = 'accent',
  className,
}: CompactProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const fillColor =
    tone === 'danger'
      ? 'var(--inv-danger, #d92d20)'
      : 'var(--inv-accent)';

  return (
    <div
      className={cn(
        'h-2 w-full max-w-[5.5rem] overflow-hidden rounded-full bg-[var(--inv-surface-muted-2,#f3ece3)]',
        '2xl:max-w-[6.25rem]',
        className,
      )}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}>
      <div
        className="h-full rounded-full transition-[width] duration-300 ease-out"
        style={{ width: `${clamped}%`, backgroundColor: fillColor }}
      />
    </div>
  );
}

export function ScanDetailsPanel({ scan }: { scan: ScanDetail }) {
  const branch = (scan.branchName || 'main').replace(/^refs\/heads\//, '');

  return (
    <div className="flex flex-col gap-1.5 rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-3 py-2.5 2xl:gap-2 2xl:px-4 2xl:py-3.5">
      <div className="flex justify-between gap-3 text-[0.75rem] 2xl:text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Build
        </span>
        <span className="text-right font-tabular text-[var(--inv-text)]">
          #{scan.buildNumber}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-[0.75rem] 2xl:text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Completed
        </span>
        <span className="break-words text-right font-tabular text-[var(--inv-text)]">
          {new Date(scan.completedAt).toLocaleString()}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-[0.75rem] 2xl:text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Branch
        </span>
        <span className="break-words text-right text-[var(--inv-text)]">
          {branch}
        </span>
      </div>
    </div>
  );
}

interface TgaStatCardProps {
  title: string;
  value: ReactNode;
  sub?: ReactNode;
  progress?: { value: number; tone?: 'accent' | 'danger' };
}

export function TgaStatCard({ title, value, sub, progress }: TgaStatCardProps) {
  return (
    <div className="rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-3 py-3 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] 2xl:px-5 2xl:py-4">
      <p className="mb-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)] 2xl:mb-1 2xl:text-[0.6875rem]">
        {title}
      </p>
      <div className="text-[1.25rem] font-bold leading-tight text-[var(--inv-text)] 2xl:text-[1.6rem]">
        {value}
      </div>
      {progress ? (
        <div className="mt-1.5 2xl:mt-2">
          <CompactProgressBar value={progress.value} tone={progress.tone} />
        </div>
      ) : null}
      {sub ? (
        <p className="mt-1.5 text-[0.6875rem] leading-snug text-[var(--inv-text-secondary)] 2xl:mt-2 2xl:text-[0.75rem]">
          {sub}
        </p>
      ) : null}
    </div>
  );
}

interface CoverageModeToggleProps {
  mode: 'lines' | 'inventory';
  onChange: (mode: 'lines' | 'inventory') => void;
}

export function CoverageModeToggle({ mode, onChange }: CoverageModeToggleProps) {
  return (
    <div
      className="inline-flex rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5"
      role="group"
      aria-label="Coverage calculation mode">
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] hover:bg-[var(--inv-surface)]',
          mode === 'lines'
            ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))]'
            : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
        )}
        onClick={() => onChange('lines')}>
        Lines of code
      </Button>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] hover:bg-[var(--inv-surface)]',
          mode === 'inventory'
            ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))]'
            : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
        )}
        onClick={() => onChange('inventory')}>
        Inventory
      </Button>
    </div>
  );
}

interface BreakdownTileProps {
  label: string;
  covered: number;
  count: number;
  coverage: number;
}

export function BreakdownTile({
  label,
  covered,
  count,
  coverage,
}: BreakdownTileProps) {
  const high = coverage >= 80;

  return (
    <div className="rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-3 py-2.5 text-center shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] 2xl:px-4 2xl:py-3.5">
      <div className="text-[1.05rem] font-bold text-[var(--inv-text)] 2xl:text-[1.3rem]">
        {covered}/{count}
      </div>
      <div className="mt-0.5 text-[0.75rem] text-[var(--inv-text-secondary)] 2xl:mt-1 2xl:text-[0.8rem]">
        {label}
      </div>
      <div
        className={cn(
          'mt-0.5 text-[0.72rem] font-semibold 2xl:mt-1 2xl:text-[0.78rem]',
          high ? 'text-[#16a34a]' : 'text-[#d92d20]',
        )}>
        {coverage.toFixed(2)}% covered
      </div>
    </div>
  );
}
